import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { tenantCommand } from '../../model/const/command';
import { LeaseAgreementCategory, RentalObjectCategory, TenantCategory, TenantCategoryToString } from '../../model/const/enums';
import { Contact, ContactIdKind } from '../../model/contact';
import { Contract } from '../../model/contract';
import { PrintGdprCommandHandler } from '../../service/command/print-gdpr-command-handler.service';
import { SendRegistrationKeyCommandHandler } from '../../service/command/send-registration-key-command-handler.service';
import { TenantFormCommandHandlerResolver } from '../../service/command/tenant-form-command-handler-resolver.service';
import { ContactService } from '../../service/data/contact.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { RentApplication } from '../../model/rent-application';
import { catchError, pairwise, startWith, switchMap, tap } from 'rxjs/operators';
import { RepayBalanceCommandHandler } from '../../service/command/repay-balance-command-handler.service';
import { of, Subscription, throwError } from 'rxjs';
import { SelectionItem } from '../../../core/component/select/select.component';
import { BrfTransferForm } from '../../model/Transfer/transfer-form';
import { CooperativeHousingTransferFormService } from '../../service/data/cooperative-housing-transfer-form.service';
import { TransferType, transferTypeToString } from '../../model/const/transfer-type';
import { CooperativeHousingTransferFormCommandHandlerResolver } from '../../service/command/cooperative-housing-transfer-form-command-handler-resolver.service';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';

import { TransferCooperativeHousingCommandHandler } from '../../service/command/transfer-cooperative-housing-command-handler.service';
import { transferCommand } from '../../command/transfer.command';

@Component({
    selector: 'cooperative-housing-transfer-form',
    templateUrl: './cooperative-housing-transfer-form.component.html',
    styleUrls: ['./cooperative-housing-transfer-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        NavigationService,
        TransferCooperativeHousingCommandHandler,
        CooperativeHousingTransferFormCommandHandlerResolver,
        PrintGdprCommandHandler,
        RepayBalanceCommandHandler,
        { provide: DataService, useExisting: CooperativeHousingTransferFormService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CooperativeHousingTransferFormComponent extends FormComponentBase<BrfTransferForm> implements OnDestroy {
  constructor(
    private _cf: FormControlExFactory,
    private fb: FormBuilder,
    activatedRoute: ActivatedRoute,
    dispatcher: CommandDispatcher,
    handlerResolver: CooperativeHousingTransferFormCommandHandlerResolver,
    dataService: CooperativeHousingTransferFormService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, dataService, messageService, navigation, dispatcher, handlerResolver);
    this.menuDefinition = [];
    this.addMenuItemDefinition('COOPERATIVE_HOUSING.UPDATE_TRANSFER', transferCommand.updateTransfer);
  }

  protected createModel(): BrfTransferForm {
    return new BrfTransferForm();
  }

  protected getFormGroup(): FormGroup {
    const form = this.fb.group({
      oldLeaseAgreementNumber: this._cf.createReadonly('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.oldLeaseAgreementNumber),
      leaseAgreementNumber: this._cf.createReadonly('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.leaseAgreementNumber),
      rentalId: this._cf.createReadonly('RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL', this.dataContext.rentalId),
      transferDate: this._cf.createMandatory('LEASE_AGREEMENT.AGREEMENT_DATE', this.dataContext.transferDate),
      amount: this._cf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_AMOUNT', this.dataContext.amount),
      transferType: this._cf.createMandatory('COOPERATIVE_HOUSING.TRANSFER_TYPE', this.dataContext.transferType),
      additionalPayment_639: this._cf.createMandatory(
        'COOPERATIVE_HOUSING.KU55.ADDITIONAL_TRANSFER_AMOUNT_639',
        this.dataContext.additionalPayment_639
      ),
      innerFund_635: this._cf.createMandatory('COOPERATIVE_HOUSING.KU55.INNER_FUND_635', this.dataContext.innerFund_635),
      sellers: this.getSellerForms(),
      buyers: this.getBuyerForms(),
    });
    return form;
  }

  transferTypes: SelectionItem[] = [
    new SelectionItem(transferTypeToString(TransferType.Purchase), TransferType.Purchase),
    new SelectionItem(transferTypeToString(TransferType.PartitionOfProperty), TransferType.PartitionOfProperty),
    new SelectionItem(transferTypeToString(TransferType.Gift), TransferType.Gift),
    new SelectionItem(transferTypeToString(TransferType.Inheritance), TransferType.Inheritance),
    new SelectionItem(transferTypeToString(TransferType.ForcedSale), TransferType.ForcedSale),
  ];

  sellerForms: FormArray;
  buyerForms: FormArray;

  ngOnInit() {
    super.ngOnInit();
  }

  private getSellerForms(): FormArray {
    this.sellerForms = this.fb.array([]);
    for (let seller of this.dataContext.sellers) {
      let form = this.fb.group({
        id: this._cf.createHidden(seller.id),
        contactId: this._cf.createMandatory('COOPERATIVE_HOUSING.OWNER', seller.contactId),
        ownedShare: this._cf.createMandatory('COOPERATIVE_HOUSING.OWNED_SHARE', seller.ownedShare),
        soldShare: this._cf.createMandatory('COOPERATIVE_HOUSING.TRANSFERRED_SHARE', seller.soldShare),
        name: this._cf.createMandatory('COOPERATIVE_HOUSING.OWNER', seller.name),
        email: this._cf.create('COMMON.EMAIL', seller.email),
        phone: this._cf.create('COMMON.PHONE_NUMBER', seller.phone),
        companyAmortization_636: this._cf.createMandatory(
          'COOPERATIVE_HOUSING.KU55.COMPANY_AMORTIZATION_636',
          seller.companyAmortization_636
        ),
        buyDate_640: this._cf.createMandatory('COOPERATIVE_HOUSING.KU55.BUY_DATE_640', seller.buyDate_640),
        buyAmount_643: this._cf.createMandatory('COOPERATIVE_HOUSING.KU55.BUY_AMOUNT_643', seller.buyAmount_643),
        innerFundBuy_644: this._cf.createMandatory('COOPERATIVE_HOUSING.KU55.INNER_FUND_BUY_644', seller.innerFundBuy_644),
        ownerAmortization_636: this._cf.createMandatory('COOPERATIVE_HOUSING.KU55.OWNER_AMORTIZATION_636', seller.ownerAmortization_636),
        acquisitionGiftOrInheritance_642: this._cf.create(
          'COOPERATIVE_HOUSING.KU55.AQUISITION_GIFT_OR_INHERITANCE_642',
          seller.acquisitionGiftOrInheritance_642
        ),
        postalAddress: this.fb.group({}),
        memberFrom: this._cf.createHidden(seller.memberFrom),
        membershipApproved: this._cf.createHidden(seller.membershipApproved),
        memberTo: this._cf.create('COOPERATIVE_HOUSING.MEMBER_TO', seller.memberTo),
      });
      this.sellerForms.push(form);
    }
    return this.sellerForms;
  }

  private getBuyerForms(): FormArray {
    this.buyerForms = this.fb.array([]);
    for (let buyer of this.dataContext.buyers) {
      let form = this.fb.group({
        id: this._cf.createHidden(buyer.id),
        contactId: this._cf.createMandatory('COOPERATIVE_HOUSING.OWNER', buyer.contactId),
        name: this._cf.createReadonly('COOPERATIVE_HOUSING.OWNER', buyer.name),
        acquiredShare: this._cf.createMandatory('COOPERATIVE_HOUSING.BOUGHT_SHARE', buyer.acquiredShare),
        email: this._cf.create('COMMON.EMAIL', buyer.email),
        phone: this._cf.create('COMMON.PHONE_NUMBER', buyer.phone),
        memberFrom: this._cf.createMandatory('COOPERATIVE_HOUSING.MEMBER_FROM', buyer.memberFrom),
        approvalDate: this._cf.create('COOPERATIVE_HOUSING.MEMBER_APPROVAL_DATE', buyer.approvalDate),
      });
      this.buyerForms.push(form);
    }

    return this.buyerForms;
  }
}
